import { recordOriginalGeometryPositions } from './meshOriginalGeometry';
import {
  recomputeGeometryBounds,
  setVertexFromObjectLocal,
} from './objectSpaceGeometry';
import { isCabinetSectionHighlightBox } from '../cabinetInterior/tallCabinetSectionHighlightBoxes';
import {isRuntimeCabinetHandle} from '../cabinetLoader/cabinetHandleRuntime';

const DEFAULT_FRONT_MESH_NAME = /^(Door[ _](Left|Right)|Drawer)/;
const DEFAULT_SIDE_BAND_RATIO = 0.28;

function isFrontMesh(mesh, frontMeshName) {
  let current = mesh;

  while (current && current.isObject3D) {
    if (current.name && frontMeshName.test(current.name)) {
      return true;
    }

    current = current.parent;
  }

  return false;
}

function getAxisSize(bounds, axis) {
  return bounds[`max${axis.toUpperCase()}`] - bounds[`min${axis.toUpperCase()}`];
}

function getPrimaryFrontAxis(bounds) {
  return getAxisSize(bounds, 'x') >= getAxisSize(bounds, 'z') ? 'x' : 'z';
}

function getMeshBoundsInInstanceSpace(mesh, instance, positions) {
  const THREE = window.THREE;
  const vertex = new THREE.Vector3();
  const bounds = {
    minX: Infinity,
    maxX: -Infinity,
    minZ: Infinity,
    maxZ: -Infinity,
  };

  mesh.updateMatrixWorld(true);

  for (let index = 0; index < positions.length; index += 3) {
    vertex.set(positions[index], positions[index + 1], positions[index + 2]);
    mesh.localToWorld(vertex);
    instance.worldToLocal(vertex);

    bounds.minX = Math.min(bounds.minX, vertex.x);
    bounds.maxX = Math.max(bounds.maxX, vertex.x);
    bounds.minZ = Math.min(bounds.minZ, vertex.z);
    bounds.maxZ = Math.max(bounds.maxZ, vertex.z);
  }

  return bounds;
}

function getPositionArray(mesh) {
  return mesh.geometry
    && mesh.geometry.attributes
    && mesh.geometry.attributes.position
    && mesh.geometry.attributes.position.array;
}

function getNineSliceAxisValue(value, sourceMin, sourceMax, targetMin, targetMax, sideBandRatio) {
  const sourceSize = sourceMax - sourceMin;
  const targetSize = targetMax - targetMin;

  if (!sourceSize || sourceSize <= 0 || !targetSize || targetSize <= 0) {
    return value;
  }

  const sourceBandSize = sourceSize * sideBandRatio;
  const targetBandSize = Math.min(sourceBandSize, targetSize * 0.45);
  const sourceMinBandEnd = sourceMin + sourceBandSize;
  const sourceMaxBandStart = sourceMax - sourceBandSize;
  const targetMinBandEnd = targetMin + targetBandSize;
  const targetMaxBandStart = targetMax - targetBandSize;

  if (value <= sourceMinBandEnd) {
    return targetMin + ((value - sourceMin) * (targetBandSize / sourceBandSize));
  }

  if (value >= sourceMaxBandStart) {
    return targetMax - ((sourceMax - value) * (targetBandSize / sourceBandSize));
  }

  const sourceMiddleSize = sourceMaxBandStart - sourceMinBandEnd;
  const targetMiddleSize = targetMaxBandStart - targetMinBandEnd;

  if (sourceMiddleSize <= 0 || targetMiddleSize < 0) {
    return value;
  }

  const middleRatio = (value - sourceMinBandEnd) / sourceMiddleSize;
  return targetMinBandEnd + (middleRatio * targetMiddleSize);
}

function resizeStraightFrontMesh(mesh, instance, options) {
  const originalGeometry = recordOriginalGeometryPositions(mesh, options.geometryTag, null, null);
  const position = mesh.geometry && mesh.geometry.attributes && mesh.geometry.attributes.position;
  const currentPositions = getPositionArray(mesh);

  if (!originalGeometry || !position || !currentPositions) {
    return;
  }

  const sourceBounds = getMeshBoundsInInstanceSpace(mesh, instance, originalGeometry.positions);
  const targetBounds = getMeshBoundsInInstanceSpace(mesh, instance, currentPositions);
  const axis = getPrimaryFrontAxis(sourceBounds);
  const axisKey = axis.toUpperCase();
  const sourceMin = sourceBounds[`min${axisKey}`];
  const sourceMax = sourceBounds[`max${axisKey}`];
  const targetMin = targetBounds[`min${axisKey}`];
  const targetMax = targetBounds[`max${axisKey}`];

  const THREE = window.THREE;
  const vertex = new THREE.Vector3();
  const originalVertex = new THREE.Vector3();
  const currentVertex = new THREE.Vector3();
  const instanceLocalVertex = new THREE.Vector3();

  for (let index = 0; index < originalGeometry.positions.length; index += 3) {
    originalVertex.set(
      originalGeometry.positions[index],
      originalGeometry.positions[index + 1],
      originalGeometry.positions[index + 2]
    );
    currentVertex.set(
      position.array[index],
      position.array[index + 1],
      position.array[index + 2]
    );

    mesh.localToWorld(originalVertex);
    instance.worldToLocal(originalVertex);
    mesh.localToWorld(currentVertex);
    instance.worldToLocal(currentVertex);

    instanceLocalVertex.copy(currentVertex);
    instanceLocalVertex[axis] = getNineSliceAxisValue(
      originalVertex[axis],
      sourceMin,
      sourceMax,
      targetMin,
      targetMax,
      options.sideBandRatio
    );

    setVertexFromObjectLocal(mesh, instance, vertex, instanceLocalVertex);

    position.array[index] = vertex.x;
    position.array[index + 1] = vertex.y;
    position.array[index + 2] = vertex.z;
  }

  position.needsUpdate = true;
  recomputeGeometryBounds(mesh.geometry);
}

// Corrects straight cabinet door/drawer fronts after the global cabinet resize.
// Each front mesh gets its own 9-slice so inner door stiles do not stretch with the cabinet middle.
export function resizeStraightFrontMeshesNineSlice(instance, options = {}) {
  if (!instance) {
    return;
  }

  const resizeOptions = {
    geometryTag: options.geometryTag || 'straightFrontMeshOriginalGeometry',
    sideBandRatio: options.sideBandRatio || DEFAULT_SIDE_BAND_RATIO,
    frontMeshName: options.frontMeshName || DEFAULT_FRONT_MESH_NAME,
  };

  instance.traverse((child) => {
    if (
      child.isMesh
      && !isCabinetSectionHighlightBox(child)
      && !isRuntimeCabinetHandle(child)
      && isFrontMesh(child, resizeOptions.frontMeshName)
    ) {
      resizeStraightFrontMesh(child, instance, resizeOptions);
    }
  });
}
